import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import LazyImage from '@/components/ui/lazy-image';
import { Helmet } from 'react-helmet-async';
import { Heart } from 'lucide-react';
import { useStore } from '@/store';
import { formatPrice, getCache, setCache, getDomain } from '@/lib/utils';
import { calculateProductDiscount } from '@/lib/discount';
import { DiscountBadge } from '@/components/ui/discount-badge';
import { Button } from '@/components/ui/button';
import { toast } from 'react-hot-toast';
import { Product } from '@/types';
import { WishlistSkeleton } from '@/components/ui/skeleton';

export function WishlistPage() {
  const { wishlist, toggleWishlist, setWishlist } = useStore();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        console.log('Wishlist IDs:', wishlist); // Debug log
        
        // Check cache first with timestamp
        const cacheKey = 'wishlist_products';
        const cachedData = getCache(cacheKey);
        const cacheTimestamp = getCache('products_cache_timestamp');
        const now = Date.now();
        const cacheValid = cacheTimestamp && (now - cacheTimestamp) < 5 * 60 * 1000; // 5 minutes

        let allProducts = cachedData && cacheValid ? cachedData : null;
        
        if (!allProducts) {
          const domain = getDomain();
          const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/products?domain=${domain}`);
          if (!response.ok) throw new Error('Failed to fetch products');
          
          const data = await response.json();
          if (!data.status) throw new Error(data.message);
          
          allProducts = data.products;
          console.log('Fetched products:', allProducts.length); // Debug log
          // Cache the products and timestamp
          setCache(cacheKey, allProducts);
          setCache('products_cache_timestamp', now);
        }
        
        const availableProducts = allProducts.filter((p: Product) => {
          if (p.status !== 1 || p.totalStock <= 0) return false;
          
          if (p.variations && p.variations.length > 0 && p.variations[0].variation !== 'Nil') {
            return p.variations.some(v => parseInt(v.v_qty) > 0);
          }
          return p.totalStock > 0;
        });

        console.log('Available products:', availableProducts.length); // Debug log
        const wishlistProducts = availableProducts.filter((p: Product) => wishlist.includes(p.id));
        console.log('Wishlist products found:', wishlistProducts.length); // Debug log
        setProducts(wishlistProducts);

        const availableIds = wishlistProducts.map(p => p.id);
        if (availableIds.length !== wishlist.length) {
          console.log('Cleaning up wishlist - removing unavailable products'); // Debug log
          setWishlist(availableIds);
        }
      } catch (err) {
        console.error('Error fetching products:', err);
        // Try to use cached data as fallback
        const cachedData = getCache('wishlist_products');
        if (cachedData) {
          const wishlistProducts = cachedData.filter((p: Product) => wishlist.includes(p.id));
          setProducts(wishlistProducts);
        }
      } finally {
        setLoading(false);
      }
    };

    if (wishlist.length > 0) {
      fetchProducts();
    } else {
      setProducts([]);
      setLoading(false);
    }
  }, [wishlist, setWishlist]);

  const handleRemoveFromWishlist = (productId: number) => {
    toggleWishlist(productId);
    toast.success('Removed from wishlist');
  };

  const isProductOutOfStock = useMemo(() => {
    return (product: Product) => {
      if (product.status !== 1) return true;
      if (product.variations && product.variations.length > 0 && product.variations[0].variation !== 'Nil') {
        return product.variations.every(v => parseInt(v.v_qty) === 0);
      }
      return parseInt(product.totalStock.toString()) === 0;
    };
  }, []);

  // Memoize the products list to prevent unnecessary re-renders
  const memoizedProducts = useMemo(() => products, [products]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">My Wishlist</h1>
          <WishlistSkeleton />
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Wishlist - Store</title>
        <meta name="description" content="View your wishlist items" />
      </Helmet>

      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">My Wishlist</h1>

          {products.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center">
              <Heart className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-4" />
              <p className="text-gray-500 dark:text-gray-400 mb-4">Your wishlist is empty</p>
              <Link to="/products">
                <Button>
                  Continue Shopping
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {memoizedProducts.map((product) => {
                const outOfStock = isProductOutOfStock(product);
                const discountInfo = calculateProductDiscount(product, 1);

                return (
                  <div
                    key={product.id}
                    className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden group relative"
                  >
                    {outOfStock && (
                      <div 
                        className="absolute inset-0 bg-black/50 flex items-center justify-center z-10 cursor-pointer"
                        onClick={() => handleRemoveFromWishlist(product.id)}
                      >
                        <div className="text-center p-4">
                          <div className="inline-flex items-center px-4 py-2 rounded-full bg-red-500 text-white font-bold mb-4">
                            SOLD OUT
                          </div>
                          <p className="text-white text-sm">Click to remove from wishlist</p>
                        </div>
                      </div>
                    )}
                    
                    {!outOfStock ? (
                      <Link to={`/product/${product.id}`}>
                        <img
                          src={product.images[0]}
                          alt={product.produt_name}
                          className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </Link>
                    ) : (
                      <img
                        src={product.images[0]}
                        alt={product.produt_name}
                        className="w-full aspect-square object-cover opacity-75"
                      />
                    )}

                    {discountInfo.hasDiscount && (
                      <div className="absolute top-2 left-2">
                        <DiscountBadge 
                          discountType={discountInfo.discountType}
                          discountAmount={discountInfo.discountAmount}
                        />
                      </div>
                    )}
                    
                    <div className="p-4">
                      {!outOfStock && (
                        <Link to={`/product/${product.id}`}>
                          <div className="flex flex-col">
                            <span className="text-lg font-bold text-gray-900 dark:text-white">
                              {formatPrice(product.sellingprice)}
                            </span>
                            {discountInfo.hasDiscount && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                Buy {discountInfo.threshold}+ for discount
                              </span>
                            )}
                          </div>
                        </Link>
                      )}

                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                        {product.category || 'Uncategorized'}
                      </p>

                      <div className="flex items-center justify-between">
                        <span className={`text-lg font-bold ${
                          outOfStock
                            ? 'text-gray-500 dark:text-gray-400'
                            : 'text-gray-900 dark:text-white'
                        }`}>
                          {formatPrice(product.sellingprice)}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleRemoveFromWishlist(product.id);
                          }}
                          className="p-2 rounded-full bg-red-50 dark:bg-red-900/20 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                        >
                          <Heart className="h-5 w-5 fill-current" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
